import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import config from "../../config";

const baseUrl = config.baseUrl;
const appsEndpoint = "/api/some-protected-route";
const authEndpoint = "/api/auth/login";
const logoutEndpoint = "/api/auth/logout";

const token = localStorage.getItem("token");
const initialUser = token ? { token } : null;

const initialState = {
  loading: false,
  isAuthenticated: !!token,
  user: initialUser,
  applications: [],
  error: null,
  twoFactorRequired: false,
};

// Thunk para login
export const login = createAsyncThunk(
  "auth/login",
  async ({ username, password, otp }, { rejectWithValue }) => {
    try {
      const userAgent = navigator.userAgent;
      const payload = { login: username, password, user_agent: userAgent };
      if (otp) payload.otp = otp;

      const response = await axios.post(`${baseUrl}${authEndpoint}`, payload, {
        headers: { "Content-Type": "application/json" },
      });

      if (response.data.twoFactorRequired) {
        return {
          twoFactorRequired: true,
          message: response.data.message,
          two_factor_enabled: true,
        };
      }

      const token        = response.data.token          || null;
      const email        = response.data?.email         || null;
      const is_group5    = response.data?.is_group5     || false;
      const departamento = response.data?.departamento
        ? response.data.departamento.departamento || response.data.departamento
        : null;
      const name           = response.data?.name           || null;
      const signature_path = response.data?.signature_path || null;
      const role           = response.data?.role           || null;
      const numeroCedula   = response.data?.numeroCedula   || null;
      const oid_usuario    = response.data?.oid            || null;

      const appsResponse = await axios.get(`${baseUrl}${appsEndpoint}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });

      localStorage.setItem("token", token);
      localStorage.setItem("username", username);

      return {
        token,
        applications: appsResponse.data.applications,
        oid_usuario,
        username,
        email,
        departamento,
        name,
        two_factor_enabled: response.data.two_factor_enabled,
        is_group5,
        signature_path,
        role,
        numeroCedula,
      };
    } catch (error) {
      console.error("Error during login process:", error);
      if (error.response) {
        if (error.response.status === 403 && error.response.data.message === "No apps") {
          return rejectWithValue("No tiene aplicaciones asignadas");
        }
        return rejectWithValue(error.response.data.message);
      }
      return rejectWithValue("Login failed or applications fetch failed");
    }
  }
);

// Thunk para logout
export const logout = createAsyncThunk(
  "auth/logout",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const savedUser = localStorage.getItem("user");
      const { token } = savedUser ? JSON.parse(savedUser) : { token: "" };
      if (token) {
        await axios.post(
          `${baseUrl}${logoutEndpoint}`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      localStorage.removeItem("user");
      dispatch(clearLogout());
    } catch (error) {
      console.error("Logout failed:", error);
      return rejectWithValue("Logout failed");
    }
  }
);

// Thunk para obtener aplicaciones
export const fetchApplications = createAsyncThunk(
  "auth/fetchApplications",
  async (_, { rejectWithValue }) => {
    if (!token) return rejectWithValue("No token available");
    try {
      const response = await axios.get(`${baseUrl}${appsEndpoint}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });
      return response.data.applications || [];
    } catch (error) {
      console.error("Error fetching applications:", error);
      return rejectWithValue("Failed to fetch applications");
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearLogout: (state) => {
      state.loading = false;
      state.isAuthenticated = false;
      state.user = null;
      state.applications = [];
      state.error = null;
      state.twoFactorRequired = false;
    },
    register: (state, action) => {
      state.user = action.payload;
    },
    setTwoFactorRequired: (state, action) => {
      state.twoFactorRequired = action.payload;
    },

    // ✅ Actualiza solo los campos del perfil que cambiaron
    // sin tocar token, oid_usuario, applications ni nada más de la sesión.
    // Se llama desde PerfilTecnico después de un POST exitoso al backend.
    //
    // Uso:
    //   dispatch(updateUserProfile({
    //     name, role, numeroCedula, signature_path
    //   }))
    updateUserProfile: (state, action) => {
      if (!state.user) return; // sesión expirada, no hacer nada
      state.user = {
        ...state.user,          // conserva token, oid_usuario, username, etc.
        ...action.payload,      // sobreescribe solo los campos enviados
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.twoFactorRequired) {
          state.twoFactorRequired = true;
          state.isAuthenticated = false;
          state.token = null;
          state.user = null;
          state.applications = [];
        } else {
          state.isAuthenticated = true;
          state.user = {
            token:              action.payload.token,
            oid_usuario:        action.payload.oid_usuario,
            username:           action.payload.username,
            email:              action.payload.email,
            departamento:       action.payload.departamento,
            name:               action.payload.name,
            two_factor_enabled: action.payload.two_factor_enabled,
            is_group5:          action.payload.is_group5,
            signature_path:     action.payload.signature_path,
            role:               action.payload.role,
            numeroCedula:       action.payload.numeroCedula,
          };
          state.token = action.payload.token;
          state.applications = action.payload.applications;
          state.twoFactorRequired = action.payload.two_factor_enabled;
        }
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.error = action.payload;
        state.twoFactorRequired = false;
      })
      .addCase(logout.pending, (state) => {
        state.loading = true;
      })
      .addCase(logout.fulfilled, (state) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.applications = [];
        state.error = null;
        state.twoFactorRequired = false;
      })
      .addCase(logout.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchApplications.fulfilled, (state, action) => {
        state.applications = action.payload;
      })
      .addCase(fetchApplications.rejected, (state, action) => {
        console.error(action.payload);
      });
  },
});

export const {
  clearLogout,
  register,
  setTwoFactorRequired,
  updateUserProfile,   // ← exportar para poder hacer dispatch en PerfilTecnico
} = authSlice.actions;

export default authSlice.reducer;